// Color helpers for widget renderers — pick readable text colors
// based on the user's chosen background.

const hexToRgb = (hex) => {
  if (!hex) return null;
  const cleaned = hex.replace("#", "");
  if (cleaned.length === 3) {
    return {
      r: parseInt(cleaned[0] + cleaned[0], 16),
      g: parseInt(cleaned[1] + cleaned[1], 16),
      b: parseInt(cleaned[2] + cleaned[2], 16),
    };
  }
  if (cleaned.length === 6 || cleaned.length === 8) {
    return {
      r: parseInt(cleaned.slice(0, 2), 16),
      g: parseInt(cleaned.slice(2, 4), 16),
      b: parseInt(cleaned.slice(4, 6), 16),
    };
  }
  return null;
};

// Relative luminance per WCAG.
export const luminance = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0.5;
  const channel = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
};

// Returns black or white text — whichever has more contrast.
export const readableText = (bg) => (luminance(bg) > 0.45 ? "#0f172a" : "#ffffff");

// Returns a muted text color appropriate for the bg lightness.
export const mutedText = (bg) => (luminance(bg) > 0.45 ? "#475569" : "#cbd5e1");

// Returns a card surface color contrasting with the bg.
export const cardSurface = (bg) => (luminance(bg) > 0.45 ? "#ffffff" : "#1e293b");

// Returns a border color appropriate for the bg lightness.
export const cardBorder = (bg) => (luminance(bg) > 0.45 ? "#0f172a" : "#475569");

// Append alpha hex to a 6-digit hex color.
export const withAlpha = (hex, alpha) => {
  if (!hex) return hex;
  const a = Math.max(0, Math.min(255, Math.round(alpha * 255)))
    .toString(16)
    .padStart(2, "0");
  return `${hex}${a}`;
};

// ── Resolved palette ──────────────────────────────────────────────────
//
// Widgets historically derived every colour from bg_color. Owners can now
// set text, card and highlight colours explicitly. Each is optional: an
// empty value keeps the derived behaviour, so a widget nobody has restyled
// renders exactly as it did before these settings existed.
//
// Use this rather than calling readableText/cardSurface directly, so the
// override and the fallback live in one place.
export const widgetPalette = (widget = {}) => {
  const bg = widget.bg_color || "#0f172a";
  return {
    bg,
    accent: widget.primary_color || "#8a74b9",
    text: widget.text_color || readableText(bg),
    muted: widget.text_color || mutedText(bg),
    cardBg: widget.card_bg_color || cardSurface(bg),
    cardBgSet: !!widget.card_bg_color,
    textSet: !!widget.text_color,
    cardText: widget.text_color || readableText(widget.card_bg_color || cardSurface(bg)),
    // Empty means "leave the highlight as the proof body carries it",
    // which is TipTap's yellow baked in as an inline style.
    highlight: widget.highlight_color || null,
    highlightText: widget.highlight_text_color || null,
  };
};

// Scoped CSS for the colours that cannot travel as props.
//
//   mark      TipTap bakes the highlight colour inline onto each <mark>, so
//             only an !important rule can repaint it.
//   .bg-white Widgets hardcode their card surface as a Tailwind class in
//             ~28 places. One rule recolours every card rather than
//             threading a prop through each component.
//
// Both are scoped to `scope` so a widget cannot restyle the studio around
// it. Returns "" when neither setting is in play.
export const widgetCss = (palette, scope = ".ss-widget-scope") => {
  if (!palette) return "";
  const rules = [];

  const markDecls = [];
  if (palette.highlight) markDecls.push(`background-color:${palette.highlight} !important`);
  if (palette.highlightText) markDecls.push(`color:${palette.highlightText} !important`);
  if (markDecls.length) rules.push(`${scope} mark{${markDecls.join(";")}}`);

  // Only when explicitly set: the derived cardSurface is already what the
  // hardcoded white represents, so applying it unasked would change every
  // existing embed.
  if (palette.cardBgSet) {
    rules.push(`${scope} .bg-white{background-color:${palette.cardBg} !important}`);
  }

  // Roughly a third of the widgets pick their body text with a fixed
  // Tailwind class rather than deriving it, so a prop never reaches them.
  // These are the classes they use for copy; accent colours are set inline
  // and are deliberately left alone.
  if (palette.textSet) {
    const bodyText = [
      ".text-slate-900", ".text-slate-800", ".text-slate-700", ".text-slate-600",
      ".text-slate-500", ".text-slate-400", ".text-white", ".text-black",
    ].map((c) => `${scope} ${c}`).join(",");
    rules.push(`${bodyText}{color:${palette.text} !important}`);
  }

  return rules.join("");
};

// Kept for callers that only want the mark rule.
export const highlightCss = (palette) => widgetCss(palette);
